<template>
<!-- eslint-disable max-len -->
<edit-page
  name="User"
  model-name="user"
  param="userId"
  page-prefix="/blade/users"
  :create="createUser"
  :fetch="getUser"
  :fetch-types="getUserTypes"
  :update="updateUser"
  :remove="deleteUser"
  :enum-names="['userRoles', 'gameSelectionOverrideOptions']"
  :stub="user"
  :validator="validator"
  :utils="utils">
<template slot-scope="{ model: user, enums: { userRoles, gameSelectionOverrideOptions }, utils: { unused }, validator, creating, editing }">
  <text-input label v-model.trim="user.firstName" :validator="validator.firstName" required max-length autofocus>First Name</text-input>

  <text-input label v-model.trim="user.lastName" :validator="validator.lastName" required max-length>Last Name</text-input>

  <text-input label v-model.trim="user.email" :validator="validator.email" required email max-length>Enter email</text-input>

  <checkbox-input v-model="user.emailVerified">Email Verified</checkbox-input>
  <checkbox-input v-model="user.neverSendEmails">Never Send Emails (automatically set if an email bounces; only applies to emails sent through the user object)</checkbox-input>
  <checkbox-input v-model="user.forceCanViewDashboards">Force Allow View Dashboards (allows non-owners/non-developers/non-admins to sign in to dashboards)</checkbox-input>
  <checkbox-input v-model="user.showOnlyAssociatedDashboards">Show Only Associated Dashboards (only show dashboards with profiles associated with an assigned workgroup/class)</checkbox-input>

  <text-input label v-if="creating" v-model.trim="user.password" :validator="validator.password" required password min-length max-length>Password</text-input>

  <array-input
    :items="user.roles"
    :disabled="validator.roles.$invalid"
    :hide-button="!canAddRoles(user.roles) || user.roles.length >= getUserRolesControlled(userRoles).length"
  >
    <h5 slot="title">Roles</h5>

    <select-input
      slot="item"
      slot-scope="{ item: role, index: i }"
      v-model="role._id"
      :disabled="!isOwnerOrBladeAdmin && `${me._id}` === `${user._id}`"
      :options="unused(getUserRolesControlled(userRoles)).by().in(user.roles).by('_id').at(i)"
      required
      autofocus
      :remove="() => { user.roles.splice(i, 1); }"
    >
      Role
    </select-input>
    <span slot="button">Add User Role</span>
  </array-input>

  <select-input
    label
    v-model="user.gameSelectionOverride"
    :options="gameSelectionOverrideOptions"
    :validator="validator.gameSelectionOverride"
    :display="o => (o.includes('Classes') ? o.replace('Classes', classTypeNamePlural) : o).replace('Games', 'Gamulations')"
    required
  >
    Gamulation Selection Override
  </select-input>

  <array-input :items="user.temporaryAssignments">
    <h5 slot="title">Temporary Content Assignments (gamulation sets and dates when access ends)</h5>

    <template slot="item" slot-scope="{ item: tempAssignment, index: i }">
      <div
        class="form-row"
        style="
          gap: 16px;
          margin-bottom: 8px;
          padding-bottom: 6px;
          border-bottom: 1px solid darkgrey;
        "
      >
        <select-input
          v-model="tempAssignment.gameSet"
          :options="gameSetList"
          use="_id"
          display="name"
          required
          autofocus
          :remove="() => { user.temporaryAssignments.splice(i, 1); }"
          :to="(id) => { return `/blade/gamesets/${id}/edit`; }"
        >
          Gamulation Set
        </select-input>
        <div>
          <h6>
            Expiration Date (access lost after this time)
          </h6>
          <datetime
            v-model="tempAssignment.expirationDate"
            type="datetime"
            :id="`dtpick-${i}`"
            use12-hour
            value-zone="UTC"
            zone="local"
          >
          </datetime>
        </div>
      </div>
    </template>
    <span slot="button">Add Temp Content Assignment</span>
  </array-input>

  <array-input :items="user.assignedGame" :hide-button="user.assignedGame.length >= games.length">
    <h5 slot="title">Assigned Gamulation(s) (overrides Active flag gamulation for this User)</h5>

    <select-input
      slot="item"
      slot-scope="{ item: game, index: i }"
      v-model="game._id"
      :options="unused(games).by('_id').in(user.assignedGame).by('_id').at(i)"
      use="_id"
      display="name"
      required
      autofocus
      :remove="() => { user.assignedGame.splice(i, 1); }"
      :to="(id) => { return `/blade/games/${id}/edit`; }"
    >
      Gamulation
    </select-input>
    <span slot="button">Add Gamulation</span>
  </array-input>

  <array-input :items="user.classes" :hide-button="user.classes.length >= classes.length">
    <h5 slot="title">Assigned {{classTypeNamePlural}} (overrides everything when set)</h5>

    <template slot="item" slot-scope="{ item: selectedClass, index: i }">
      <div class="form-row">
        <select-input
          class="col-sm-8"
          v-model="selectedClass._id"
          :options="unused(classes).by('_id').in(user.classes).by('_id').at(i)"
          use="_id"
          display="className"
          required
          autofocus
          :remove="() => { user.classes.splice(i, 1); }"
          :to="(id) => { return `/blade/classes/${id}/edit`; }"
        >
          {{classTypeName}}
        </select-input>
        <checkbox-input class="col-sm-4" :value="getPaymentStatus(user, selectedClass)" @input="(checked) => { setPaymentStatus(user, selectedClass, checked); }">Payment Status</checkbox-input>
      </div>
    </template>
    <span slot="button">Add {{classTypeName}}</span>
  </array-input>

  <array-input :items="user.gameSets" :hide-button="user.gameSets && user.gameSets.length >= gameSetList.length">
    <h5 slot="title">Assigned Gamulation Set(s) (defines the sets of games that are visible/available to the user)</h5>

    <select-input
      slot="item"
      slot-scope="{ item: gameSet, index: i }"
      v-model="gameSet._id"
      :options="unused(gameSetList).by('_id').in(user.gameSets).by('_id').at(i)"
      use="_id"
      display="name"
      required
      autofocus
      :remove="() => { user.gameSets.splice(i, 1); }"
      :to="(id) => { return `/blade/gamesets/${id}/edit`; }"
    >
      Gamulation Set
    </select-input>
    <span slot="button">Add Gamulation Set</span>
  </array-input>

  <div v-if="user.registrationLinkUsed">
    <select-input
      label
      v-model="user.registrationLinkUsed"
      :options="[user.registrationLinkUsed]"
      display="name"
      disabled
      :to="(id) => { return `/blade/registration-links/${user.registrationLinkUsed._id}/edit`; }"
    >
      Registration Link Used
    </select-input>
  </div>
  <div v-else style="margin-bottom: 10px;">
    <b-input value="No registration link used" disabled></b-input>
  </div>

  <router-link v-if="editing" :to="`/blade/users/${$route.params.userId}/password`" class="btn btn-secondary" style="padding: 5px;">
    <span style="margin: 0 3px;">Change Password</span>
  </router-link>
</template>
</edit-page>
</template>

<script>
import { required, email as isEmail, minLength, maxLength, requiredIf } from 'vuelidate/lib/validators';
import { mapActions, mapGetters } from 'vuex';
import { v4 as uuidv4 } from 'uuid';
import { Datetime } from 'vue-datetime';

import { unused } from '../../../utils/utils';

import EditPage from '../../ui/pages/EditPage';

import ArrayInput from '../../ui/inputs/ArrayInput';
import SelectInput from '../../ui/inputs/SelectInput';
import TextInput from '../../ui/inputs/TextInput';
import CheckboxInput from '../../ui/inputs/CheckboxInput';
import { isTransactionStatusPaid } from '../../../utils/braintree-util';

export default {
  components: {
    EditPage,
    ArrayInput,
    SelectInput,
    Datetime,
    TextInput,
    CheckboxInput
  },
  data() {
    return {
      gameSetList: [],
      utils: {
        unused
      },
      user: {
        _id: '',
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        neverSendEmails: false,
        roles: [],
        assignedGame: [],
        classes: [],
        classTransactions: [],
        gameSets: [],
        emailVerified: true,
        forceCanViewDashboards: false,
        showOnlyAssociatedDashboards: false,
        gameSelectionOverride: 'Default'
      },
      validator: {
        firstName: {
          required,
          maxLength: maxLength(35)
        },
        lastName: {
          required,
          maxLength: maxLength(35)
        },
        email: {
          required,
          email: isEmail,
          minLength: minLength(3),
          maxLength: maxLength(254)
        },
        roles: {
          $each: {
            required
          }
        },
        password: {
          required: requiredIf(() => !this.$route.params.userId),
          minLength: minLength(6),
          maxLength: maxLength(128)
        },
        gameSelectionOverride: {
          required
        }
      },
      games: [],
      classes: [],
    };
  },
  async created() {
    try {
      this.games = await this.getGames();
      this.gameSetList = await this.getGameSets();
      this.classes = await this.getClasses();
    } catch (_) {
      const message = `Error fetching gamulations and ${this.classTypeNameLowerPlural}, please try again`;
      this.flashDanger({ message });
    }
  },
  computed: {
    ...mapGetters([
      'isOwnerOrBladeAdmin',
      'isNonBladeAdminContent',
      'me',
      'classTypeName',
      'classTypeNamePlural',
      'classTypeNameLower',
      'classTypeNameLowerPlural',
    ])
  },
  methods: {
    ...mapActions([
      'createUser',
      'getUser',
      'getUserTypes',
      'updateUser',
      'deleteUser',
      'getGames',
      'getGameSets',
      'getClasses',
      'flashDanger',
    ]),
    getUserRolesControlled(userRoles) {
      if (this.isOwnerOrBladeAdmin) {
        return userRoles;
      }
      if (this.isNonBladeAdminContent) {
        return userRoles.filter(r => (r !== 'owner' && r !== 'developer' && r !== 'blade'));
      }
      return userRoles.filter(r => (r !== 'owner' && r !== 'developer' && r !== 'blade' && r !== 'admin-content'));
    },
    canAddRoles(roles) {
      return this.isOwnerOrBladeAdmin || roles.length < 1;
    },
    getPaymentStatus(user, selectedClass) {
      const idCheck = `${selectedClass._id}`;
      for (let i = 0; i < this.classes.length; ++i) {
        if ((`${this.classes[i]._id}` === idCheck)
          && (this.classes[i].price == null || this.classes[i].price === 0)) {
          return true;
        }
      }
      const classTransactions = user.classTransactions || [];
      const transactions = classTransactions.filter(t => `${t._id}` === idCheck);

      let didPay = false;
      // eslint-disable-next-line no-restricted-syntax
      for (const transaction of transactions) {
        const transactionPaid = (transaction && transaction.status &&
          isTransactionStatusPaid(transaction.status));
        didPay = didPay ||
          transactionPaid;
      }
      return didPay;
    },
    setPaymentStatus(user, selectedClass, checked) {
      const idCheck = `${selectedClass._id}`;
      if (!checked) {
        for (let i = 0; i < this.classes.length; ++i) {
          if ((`${this.classes[i]._id}` === idCheck)
            && (this.classes[i].price == null || this.classes[i].price === 0)) {
            const message = `This ${this.classTypeNameLower} is free. Payment status will not be changed.`;
            this.flashDanger({ message });
            return;
          }
        }
      }
      if (user.classTransactions == null) {
        user.classTransactions = [];
      }
      for (let i = 0; i < user.classTransactions.length; ++i) {
        if (`${user.classTransactions[i]._id}` === idCheck) {
          if (checked && !(isTransactionStatusPaid(user.classTransactions[i].status))) {
            user.classTransactions[i].status = 'coupon-success';
          } else if (isTransactionStatusPaid(user.classTransactions[i].status)) {
            // TODO: allow for voiding/refunding in-app?
            const message =
              'User has purchased or initiated a purchase. Payment status will not be changed. You should void or refund the user via the payment gateway instead.';
            this.flashDanger({ message });
            return;
          } else if (user.classTransactions[i].status === 'coupon-success') {
            user.classTransactions.splice(i, 1);
            return;
          }
        }
      }
      // no match
      if (checked) {
        user.classTransactions.push({
          _id: selectedClass._id,
          transactionId: uuidv4(),
          status: 'coupon-success',
          lastUpdated: new Date()
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
</style>



// WEBPACK FOOTER //
// src/components/blade/pages/UserEdit.vue